import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import OnboardingModalShell, {
  onboardingErrorClass,
  onboardingInputClass,
} from '../components/OnboardingModalShell';
import {
  fetchPublicMembershipConfig,
  redeemArtmoCodes,
  completeOnboarding,
  normalizeStudioApiError,
} from '../services/studioOnboardingApi';

const REASON_COPY = {
  not_found: 'We could not find this code.',
  already_used: 'This code has already been redeemed.',
  wrong_type: 'This code is not a valid Artmo code.',
  inactive: 'This code is not active.',
  expired: 'This code has expired.',
  duplicate_in_request: 'You entered this code more than once.',
};

export default function StudioOnboardingPage() {
  const navigate = useNavigate();
  const { user, refreshMembership } = useAuth();
  const [step, setStep] = useState('welcome');
  const [config, setConfig] = useState({
    premiumAddonMonthlyPrice: 200,
    currency: 'INR',
    isUpgradeEnabled: true,
    razorpayConfigured: false,
    premiumSimulateAllowed: true,
  });
  const [codeRows, setCodeRows] = useState(['']);
  const [validationResults, setValidationResults] = useState([]);
  const [validCodes, setValidCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const noBookNavLock = useRef(false);

  useEffect(() => {
    fetchPublicMembershipConfig()
      .then((c) => {
        if (c && c.ok !== false) {
          setConfig({
            premiumAddonMonthlyPrice: c.premiumAddonMonthlyPrice ?? 200,
            currency: c.currency || 'INR',
            isUpgradeEnabled: c.isUpgradeEnabled !== false,
            razorpayConfigured: !!c.razorpayConfigured,
            premiumSimulateAllowed: c.premiumSimulateAllowed !== false,
          });
        }
      })
      .catch(() => {});
  }, []);

  const handleNoBookStore = (dest) => {
    if (noBookNavLock.current) return;
    noBookNavLock.current = true;
    setError('');
    if (dest === 'create') {
      navigate('/create-books', { replace: true });
    } else {
      navigate('/membership', {
        replace: true,
        state: { isRenewal: false, from: '/onboarding/studio-access' },
      });
    }
    void (async () => {
      try {
        await completeOnboarding('no_book_customer');
        if (user?.uid) {
          try {
            await refreshMembership(user.uid, { force: true });
          } catch (_) {
            /* non-blocking */
          }
        }
      } catch (e) {
        console.warn('Studio onboarding (no_book_customer):', normalizeStudioApiError(e));
      }
    })();
  };

  const runRedeem = async () => {
    const codes = codeRows.map((c) => c.trim()).filter(Boolean);
    if (!codes.length) {
      setError('Add at least one Artmo code.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await redeemArtmoCodes(codes);
      const results = data.results || [];
      setValidationResults(results);

      const okCodes = results.filter((r) => r.valid).map((r) => r.code);
      setValidCodes(okCodes);

      if (!okCodes.length) {
        setError('None of the codes could be applied. Check the messages below.');
        return;
      }

      const months = data.monthsGranted ?? okCodes.length;
      void refreshMembership(user.uid, { force: true }).catch((err) => {
        console.warn('refreshMembership after redeem (background):', err?.message || err);
      });
      navigate('/onboarding/artmo-success', {
        replace: true,
        state: { monthsGranted: months, periodEnd: data.periodEnd },
      });
    } catch (e) {
      setError(normalizeStudioApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fffdf9] via-[#fff6ec] to-[#ffefd8]">
      <AnimatePresence mode="wait">
        {step === 'welcome' && (
          <OnboardingModalShell
            key="welcome"
            title="Welcome to Artmosphere Studio"
            ariaLabelledBy="studio-onboarding-welcome-title"
          >
            <p className="mb-4 text-sm leading-relaxed text-[#5A4632] sm:text-base">
              Let&apos;s get your access started.
            </p>
            <p className="mb-6 font-semibold text-[#2F251B]">Are you an Artmosphere book customer?</p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => setStep('artmo')}
                className="art-btn-primary min-h-[48px] rounded-full px-6 text-sm font-semibold sm:flex-1"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setStep('no_book_store')}
                className="art-btn-secondary min-h-[48px] rounded-full px-6 text-sm font-semibold sm:flex-1"
              >
                No
              </button>
            </div>
          </OnboardingModalShell>
        )}

        {step === 'no_book_store' && (
          <OnboardingModalShell
            key="no_book_store"
            wide
            title="Visit Artmosphere Book Store"
            ariaLabelledBy="studio-onboarding-bookstore-title"
          >
            <p className="mb-6 text-sm leading-relaxed text-[#5A4632]">
              When you buy Artmosphere books, you unlock complimentary access to Artmosphere Studio.
              Explore the bookstore or choose a membership plan when you&apos;re ready.
            </p>
            {error ? <div className={`mb-4 ${onboardingErrorClass}`}>{error}</div> : null}
            <div className="flex flex-col gap-3">
              <button
                type="button"
                disabled={loading}
                onClick={() => handleNoBookStore('create')}
                className="art-btn-primary min-h-[48px] w-full rounded-full text-sm font-semibold disabled:opacity-50"
              >
                Artmosphere Book Store
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => handleNoBookStore('membership')}
                className="art-btn-secondary min-h-[48px] w-full rounded-full text-sm font-semibold disabled:opacity-50"
              >
                Get Artmosphere Studio Membership
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => {
                  setError('');
                  setStep('welcome');
                }}
                className="text-sm font-medium text-[#8A7357] underline transition hover:text-[#2F251B]"
              >
                Back
              </button>
            </div>
          </OnboardingModalShell>
        )}

        {step === 'artmo' && (
          <OnboardingModalShell
            key="artmo"
            wide
            title="Enter your Artmo code"
            ariaLabelledBy="studio-onboarding-artmo-title"
          >
            <p className="mb-4 text-sm leading-relaxed text-[#5A4632]">
              Each valid code adds one month of Base access. You can enter multiple codes at once.
            </p>

            <div className="mb-4 space-y-3">
              {codeRows.map((row, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    value={row}
                    onChange={(e) => {
                      const next = [...codeRows];
                      next[idx] = e.target.value;
                      setCodeRows(next);
                    }}
                    className={onboardingInputClass}
                    placeholder={`Code ${idx + 1}`}
                    autoCapitalize="characters"
                  />
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setCodeRows((r) => [...r, ''])}
              className="mb-4 text-sm font-semibold text-[#B45309] transition hover:text-[#92400E]"
            >
              + Add another code
            </button>

            {validationResults.length > 0 && (
              <div className="mb-4 rounded-2xl border border-[#F5D99B]/70 bg-[#FFF9EF]/90 p-4 text-left text-sm">
                <p className="mb-2 font-semibold text-[#2F251B]">Summary</p>
                <ul className="space-y-2">
                  {validationResults.map((r) => (
                    <li key={r.code} className="flex justify-between gap-2">
                      <span className="font-mono text-xs text-[#5A4632]">{r.code}</span>
                      <span className={r.valid ? 'font-medium text-emerald-700' : 'text-rose-700'}>
                        {r.valid ? 'Valid ✓' : REASON_COPY[r.reason] || r.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {error ? <div className={`mb-4 ${onboardingErrorClass}`}>{error}</div> : null}

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                disabled={loading}
                onClick={runRedeem}
                className="art-btn-primary min-h-[48px] flex-1 rounded-full text-sm font-semibold disabled:opacity-50"
              >
                {loading ? 'Validating & redeeming…' : 'Confirm & redeem'}
              </button>
            </div>
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                setError('');
                setValidationResults([]);
                setValidCodes([]);
                setStep('welcome');
              }}
              className="mt-4 text-sm font-medium text-[#8A7357] underline transition hover:text-[#2F251B]"
            >
              Back
            </button>
          </OnboardingModalShell>
        )}
      </AnimatePresence>
    </div>
  );
}
